import { useEffect, useMemo, useRef, useState } from "react";
import InfoTooltip from "../../components/common/InfoTooltip";
import { SALON_SPEAK_TOOLTIPS } from "../../constants/salonSpeakTooltips";
import {
  canRecordAudio,
  canRecognizeSpeech,
  scoreAttempt,
  speakText,
  startVoiceCapture,
} from "./speechUtils";

const CATEGORIES = [
  "All",
  "Tools & Products",
  "Nail Shapes",
  "Design Terms",
  "Timing & Scheduling",
  "Pricing",
  "Waxing",
  "Client Preferences",
];

const favoriteKey = (type, id) => `${type}:${id}`;

const DEFINITION_TEMPLATES = {
  "Tools & Products": {
    en: (word) =>
      `${word} is a tool or product used in nail and beauty services to improve technique, safety, and service results.`,
    vi: (word) =>
      `${word} là dụng cụ hoặc sản phẩm dùng trong dịch vụ nail và làm đẹp để hỗ trợ kỹ thuật, an toàn và chất lượng dịch vụ.`,
  },
  "Nail Shapes": {
    en: (word) =>
      `${word} is a nail shape style used to create the client's preferred look, balance, and durability.`,
    vi: (word) =>
      `${word} là kiểu dáng móng dùng để tạo hình theo sở thích khách, bảo đảm độ cân đối và độ bền.`,
  },
  "Design Terms": {
    en: (word) =>
      `${word} is a nail art term that describes a design effect or style used in salon beauty services.`,
    vi: (word) =>
      `${word} là thuật ngữ nail art mô tả hiệu ứng hoặc phong cách thiết kế trong dịch vụ làm đẹp tại tiệm.`,
  },
  "Timing & Scheduling": {
    en: (word) =>
      `${word} is a scheduling term used to manage appointment timing, technician flow, and client expectations.`,
    vi: (word) =>
      `${word} là thuật ngữ sắp lịch dùng để quản lý thời gian hẹn, luồng làm việc của thợ và kỳ vọng của khách.`,
  },
  Pricing: {
    en: (word) =>
      `${word} is a pricing term used to explain service cost, add-ons, and payment details clearly in the salon.`,
    vi: (word) =>
      `${word} là thuật ngữ về giá dùng để giải thích rõ chi phí dịch vụ, phụ phí và thanh toán tại tiệm.`,
  },
  Waxing: {
    en: (word) =>
      `${word} is a waxing term used for hair-removal services, comfort checks, and post-service skin care guidance.`,
    vi: (word) =>
      `${word} là thuật ngữ tẩy lông dùng trong quy trình dịch vụ, kiểm tra độ thoải mái và hướng dẫn chăm sóc da sau dịch vụ.`,
  },
  "Client Preferences": {
    en: (word) =>
      `${word} describes a client preference used to personalize nail and beauty service choices and final results.`,
    vi: (word) =>
      `${word} mô tả sở thích của khách để cá nhân hóa lựa chọn dịch vụ nail/làm đẹp và kết quả cuối cùng.`,
  },
};

const getWordDefinitions = (word) => {
  const manualEn = String(word?.definitionEn || "").trim();
  const manualVi = String(word?.definitionVi || "").trim();
  if (manualEn && manualVi) {
    return { en: manualEn, vi: manualVi };
  }

  const template = DEFINITION_TEMPLATES[word?.category];
  if (template) {
    return {
      en: template.en(word.word),
      vi: template.vi(word.vi || word.word),
    };
  }

  return {
    en: `${word.word} is a salon vocabulary term used in nail and beauty services.`,
    vi: `${word.vi || word.word} là thuật ngữ dùng trong dịch vụ nail và làm đẹp.`,
  };
};

export default function SalonSpeakVocabulary({
  vocabulary,
  favorites,
  onToggleFavorite,
  onSaveAttempt,
  salonMode,
  onToggleSalonMode,
}) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [selectedId, setSelectedId] = useState(vocabulary?.[0]?.id || "");
  const [recordingId, setRecordingId] = useState("");
  const [feedbackById, setFeedbackById] = useState({});
  const [error, setError] = useState("");
  const recorderRef = useRef(null);
  const playbackUrlByIdRef = useRef({});

  useEffect(() => {
    return () => {
      if (typeof URL === "undefined") return;
      Object.values(playbackUrlByIdRef.current).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      playbackUrlByIdRef.current = {};
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (vocabulary || []).filter((item) => {
      if (category !== "All" && item.category !== category) return false;
      if (!q) return true;
      const definitions = getWordDefinitions(item);
      const hay = `${item.word} ${item.vi} ${definitions.en} ${definitions.vi} ${(
        item.examples || []
      ).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [vocabulary, category, query]);

  const selectedWord = useMemo(
    () => filtered.find((item) => item.id === selectedId) || filtered[0] || null,
    [filtered, selectedId]
  );

  const stopRecord = () => {
    recorderRef.current?.stop?.();
    recorderRef.current = null;
  };

  const runPractice = async (word) => {
    if (!word || recordingId) return;
    setError("");
    setRecordingId(word.id);

    const complete = async ({ transcript, audioBlob }) => {
      let playbackUrl = "";
      if (audioBlob && typeof URL !== "undefined") {
        const previousUrl = playbackUrlByIdRef.current[word.id];
        if (previousUrl) {
          URL.revokeObjectURL(previousUrl);
        }
        playbackUrl = URL.createObjectURL(audioBlob);
        playbackUrlByIdRef.current[word.id] = playbackUrl;
      }

      const scored = scoreAttempt({
        transcript: transcript || "",
        importantWords: [word.word],
        attemptRecorded: true,
      });
      const total = transcript ? scored.total : Math.max(scored.total, 60);
      setFeedbackById((prev) => ({
        ...prev,
        [word.id]: {
          transcript: transcript || "Transcript unavailable",
          score: total,
          playbackUrl: playbackUrl || prev[word.id]?.playbackUrl || "",
          suggestions: transcript
            ? scored.suggestions
            : ["Transcript unavailable. Self-check pronunciation and try again."],
        },
      }));

      if (playbackUrl) {
        try {
          const previewAudio = new Audio(playbackUrl);
          previewAudio.play().catch(() => null);
        } catch (err) {
          // no-op
        }
      }

      await onSaveAttempt?.({
        attempt_type: "vocab",
        content_id: word.id,
        transcript,
        score: total,
        keywords_hit: scored.keywordsHit,
      });
      setRecordingId("");
    };

    try {
      const controller = await startVoiceCapture({
        salonMode,
        forceMediaRecorder: true,
        onComplete: (result) => complete(result),
        onError: (reason) => setError(reason || "Recording failed"),
      });
      recorderRef.current = controller;
    } catch (err) {
      setRecordingId("");
      setError("Recording not available on this browser.");
      if (!canRecognizeSpeech() && !canRecordAudio()) {
        await complete({ transcript: null });
      }
    }
  };

  const renderWordCard = (word) => {
    const isFavorite = favorites.has(favoriteKey("vocab", word.id));
    const feedback = feedbackById[word.id];
    const definitions = getWordDefinitions(word);
    return (
      <div key={word.id} className="rounded-xl border border-slate-200 bg-white p-3 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-slate-900">{word.word}</div>
            <div className="text-xs text-slate-500">{word.ipaOrHint}</div>
            <div className="text-xs text-slate-600 mt-1">{word.vi}</div>
          </div>
          <button
            type="button"
            onClick={() => onToggleFavorite?.("vocab", word.id)}
            className={`rounded-full border px-2 py-1 text-xs font-semibold ${
              isFavorite
                ? "border-amber-300 bg-amber-50 text-amber-700"
                : "border-slate-300 text-slate-600"
            }`}
          >
            {isFavorite ? "Saved" : "Favorite"}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              void speakText(word.word, 0.75);
            }}
            className="rounded-full border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700"
          >
            Slow
          </button>
          <button
            type="button"
            onClick={() => {
              void speakText(word.word, 1);
            }}
            className="rounded-full border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700"
          >
            Normal
          </button>
          <button
            type="button"
            onClick={recordingId === word.id ? stopRecord : () => runPractice(word)}
            className={`rounded-full px-3 py-1 text-xs font-semibold text-white ${
              recordingId === word.id ? "bg-amber-500" : "bg-rose-500"
            }`}
          >
            {recordingId === word.id ? "Stop" : "Practice"}
          </button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 space-y-1">
          <div>
            <span className="font-semibold text-slate-900">Definition (EN):</span> {definitions.en}
          </div>
          <div>
            <span className="font-semibold text-slate-900">Định nghĩa (VI):</span> {definitions.vi}
          </div>
        </div>

        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Sample Use Sentences
        </div>
        <ul className="list-disc pl-4 text-sm text-slate-700 space-y-1">
          {(word.examples || []).slice(0, 3).map((example) => (
            <li key={example}>{example}</li>
          ))}
        </ul>

        {feedback ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-2 text-xs text-slate-700 space-y-1">
            {feedback.playbackUrl ? (
              <audio controls preload="none" className="w-full">
                <source src={feedback.playbackUrl} />
                Your browser does not support audio playback.
              </audio>
            ) : null}
            <div>
              <span className="font-semibold">Transcript:</span> {feedback.transcript}
            </div>
            <div>
              <span className="font-semibold">Score:</span> {feedback.score}/100
            </div>
            {feedback.suggestions?.length ? <div>{feedback.suggestions[0]}</div> : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-4 pb-2">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2">
            Vocabulary Practice
            <InfoTooltip text={SALON_SPEAK_TOOLTIPS.vocab.record} label="Practice" />
            <InfoTooltip text={SALON_SPEAK_TOOLTIPS.vocab.favorite} label="Favorite" />
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            <span>Salon mode</span>
            <input
              type="checkbox"
              checked={Boolean(salonMode)}
              onChange={(event) => onToggleSalonMode?.(event.target.checked)}
              className="h-4 w-4"
            />
          </label>
        </div>

        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search words"
          className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm"
        />

        <div className="flex gap-2 overflow-x-auto pb-1">
          {CATEGORIES.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setCategory(item)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${
                category === item
                  ? "bg-slate-900 text-white"
                  : "border border-slate-300 text-slate-700"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {error ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            {error}
          </div>
        ) : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)]">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
            Word List ({filtered.length})
          </div>
          <div className="max-h-[560px] overflow-y-auto divide-y divide-slate-200">
            {filtered.map((word) => (
              <button
                key={word.id}
                type="button"
                onClick={() => setSelectedId(word.id)}
                className={`w-full px-4 py-3 text-left ${
                  selectedWord?.id === word.id ? "bg-rose-50" : "hover:bg-slate-50"
                }`}
              >
                <div className="text-sm font-semibold text-slate-900">{word.word}</div>
                <div className="text-xs text-slate-500">{word.category}</div>
              </button>
            ))}
            {!filtered.length ? (
              <div className="px-4 py-4 text-sm text-slate-500">No words match your search.</div>
            ) : null}
          </div>
        </div>

        <div className="space-y-3">
          {selectedWord ? renderWordCard(selectedWord) : null}
          <div className="grid gap-3 sm:grid-cols-2 lg:hidden">
            {filtered.slice(0, 8).map((word) => renderWordCard(word))}
          </div>
        </div>
      </div>
    </div>
  );
}
